<template>
  <!-- 托盘号箱号扫描确认以及扫描出库 — ① 托盘号(파렛트번호) 스캔 → ② 箱号(박스번호) 연속 스캔 → 그 파렛트에 매핑.
       다음 托盘号를 찍기 전까지 찍은 박스는 전부 그 파렛트로 간다. 저장은 기존 /api/labels/mapBoxToPallet 그대로.
       无托盘号 扫描出库(= 예전 NOPALLET 버튼·NOPALLET 스캔) — 박스만 연속 스캔 → 그 박스의 출고대기 건이 바로 출고완료(2026-10 YZY).
       직전 박스 하나는 撤销(되돌리기) 가능(서버가 10분 안·이 경로가 남긴 이력만 허용) -->
  <div class="pallet-scan">
    <div class="d-flex align-items-center flex-wrap pallet-scan__row">
      <div :class="['pallet-scan__active', { 'is-empty': !activePallet, 'is-out': scanOut }]">
        <small>{{ scanOut ? '无托盘号 · 扫描出库(파렛트 없이 스캔 출고)' : '当前托盘(현재 파렛트)' }}</small>
        <div>{{ scanOut ? '📤 扫描出库' : (activePallet || '—') }}</div>
      </div>
      <input ref="scan" v-model="scanValue" type="text" class="form-control form-control-sm" style="width:280px"
             :placeholder="scanOut ? '连续扫箱号 → 直接出库(박스 스캔 → 바로 출고완료)' : (activePallet ? '连续扫箱号(박스번호 연속 스캔)' : '先扫托盘号(파렛트번호 먼저)')"
             :disabled="busy" @keyup.enter="onScan">
      <b-spinner v-if="busy" small></b-spinner>
      <b-button v-if="activePallet" size="sm" variant="outline-secondary" @click="clearPallet">{{ scanOut ? '结束扫描出库' : '结束托盘' }}<small>({{ scanOut ? '스캔 출고 끝' : '파렛트 해제' }})</small></b-button>
      <!-- 扫描出库 停用(2026-10-06 YZY) — 출고완료는 출고리스트 업로드로만 -->
      <b-button size="sm" variant="outline-secondary" disabled>📤 {{ scanOutOffMsg }}</b-button>
      <b-button v-if="lastOut" size="sm" variant="warning" :disabled="busy" @click="undoLast">↩ 撤销 {{ lastOut.label }}<small>(직전 박스 출고 되돌리기)</small></b-button>
      <b-button size="sm" variant="outline-info" @click="openPhone">📱 手机扫码<small>(핸드폰)</small></b-button>
    </div>
    <!-- 핸드폰으로 열기 — 직원 계정으로 로그인해서 쓴다(QR 토큰 아님) -->
    <b-modal v-model="phone.show" hide-footer size="sm" title="📱 手机扫码(핸드폰 스캔)">
      <div class="text-center">
        <img v-if="phone.qr" :src="phone.qr" alt="" style="width:220px;height:220px">
        <div class="small mt-2" style="word-break:break-all">{{ phone.url }}</div>
        <div class="small text-muted mt-1">用手机扫码打开，用员工账号登录(핸드폰으로 열고 직원 계정으로 로그인)</div>
      </div>
    </b-modal>
    <div class="small text-muted mt-1">托盘号格式(파렛트번호 형식): 名称+日期_P01 — 其他视为箱号(그 밖의 값은 박스번호로 봅니다)</div>
    <div class="row mt-2">
      <div class="col-md-6">
        <h6 class="small font-weight-bold">{{ scanOut ? '📤 已扫描出库箱号(스캔 출고한 박스)' : '📦 本托盘已扫箱号(이 파렛트에 매핑한 박스)' }} {{ mappedHere.length }}</h6>
        <div class="pallet-scan__list">
          <div v-for="b in mappedHere" :key="'m' + b">{{ b }}</div>
          <div v-if="!mappedHere.length" class="text-muted">—</div>
        </div>
      </div>
      <div class="col-md-6">
        <h6 class="small font-weight-bold">📝 扫描记录(스캔 기록)</h6>
        <div class="pallet-scan__list">
          <div v-for="(l, i) in logs" :key="'l' + i" :class="l.ok ? 'text-success' : 'text-danger'">{{ l.time }} · {{ l.text }}</div>
          <div v-if="!logs.length" class="text-muted">—</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import qrcode from 'qrcode-generator';
import { looksLikePallet, mapBoxToPallet, scanOutBox, undoScanOut, nowText, SCAN_OUT_OFF_MSG } from './putawayScan';

const PHONE_QUERY = '?mode=pallet';

const MAX_LOGS = 50;

export default {
  name: 'PalletBoxScan',
  props: {
    // 오늘 만든 파렛트 라벨(있으면 托盘号 확인에 쓴다). 없어도 형식만 맞으면 받는다
    pallets: { type: Array, default: () => [] }
  },
  data() {
    return { activePallet: '', scanValue: '', busy: false, logs: [], mapped: {}, lastOut: null, phone: { show: false, url: '', qr: '' }, scanOutOffMsg: SCAN_OUT_OFF_MSG };
  },
  computed: {
    mappedHere() {
      return this.mapped[this.activePallet] || [];
    },
    // NOPALLET = 파렛트 없이 스캔 출고 모드
    scanOut() {
      return this.activePallet === 'NOPALLET';
    }
  },
  methods: {
    openPhone() {
      const url = window.location.origin + '/m/putaway' + PHONE_QUERY;
      const qr = qrcode(0, 'M');
      qr.addData(url);
      qr.make();
      this.phone = { show: true, url, qr: qr.createDataURL(6, 4) };
    },
    log(text, ok) {
      this.logs.unshift({ time: nowText(), text, ok: !!ok });
      if (this.logs.length > MAX_LOGS) this.logs.length = MAX_LOGS;
    },
    focus() {
      this.$nextTick(() => { if (this.$refs.scan) this.$refs.scan.focus(); });
    },
    setPallet(p) {
      // NOPALLET(=扫描出库)은 停用 — 출고완료는 출고리스트 업로드로만
      if (p === 'NOPALLET') { this.log('📤 ' + SCAN_OUT_OFF_MSG, false); this.focus(); return; }
      const known = p === 'NOPALLET' || !this.pallets.length || this.pallets.some(x => x.labelId === p);
      this.activePallet = p;
      if (p === 'NOPALLET') this.log('📤 无托盘号 扫描出库 — 扫箱号即出库(박스를 찍으면 바로 출고완료)', true);
      else this.log('🔲 托盘(파렛트) ' + p + (known ? '' : ' — 今天的标签里没有(오늘 만든 라벨에 없음)'), known);
      this.focus();
    },
    clearPallet() {
      this.activePallet = '';
      this.focus();
    },
    async onScan() {
      const v = (this.scanValue || '').trim();
      this.scanValue = '';
      if (!v) return;
      if (looksLikePallet(v)) { this.setPallet(v.toUpperCase() === 'NOPALLET' ? 'NOPALLET' : v); return; }
      if (!this.activePallet) {
        this.log('请先扫托盘号(파렛트번호를 먼저 스캔하세요): ' + v, false);
        this.focus();
        return;
      }
      if (this.scanOut) { await this.scanOutOne(v); return; }
      const pallet = this.activePallet;
      this.busy = true;
      try {
        await mapBoxToPallet(v, pallet);
        const list = this.mapped[pallet] || [];
        if (!list.includes(v)) this.$set(this.mapped, pallet, [v].concat(list));
        this.log('✅ ' + v + ' → ' + pallet, true);
        this.$emit('mapped', { box: v, pallet });
      } catch (e) {
        console.error('박스→파렛트 매핑 오류:', e);
        this.log('✖ ' + v + ' — ' + e.message, false);
      } finally {
        this.busy = false;
        this.focus();
      }
    },
    // 无托盘号 扫描出库 — 박스 하나의 출고대기 건 전부 출고완료
    async scanOutOne(v) {
      this.busy = true;
      try {
        const res = await scanOutBox(v);
        if (!res.shipped.length) {
          const st = res.others.length ? ' (' + [...new Set(res.others.map(o => o.processStatus))].join('/') + ')' : '';
          this.log('✖ ' + v + ' — 箱内没有出库待发(출고대기 건 없음)' + st, false);
          return;
        }
        const qty = res.shipped.reduce((s, x) => s + (x.수량 || 0), 0);
        const list = this.mapped.NOPALLET || [];
        this.$set(this.mapped, 'NOPALLET', [v + ' · ' + res.shipped.length + '件/' + qty + '个'].concat(list));
        this.lastOut = Object.assign({ label: v }, res);
        this.log('📤 ' + v + ' → 已出库(출고완료) ' + res.shipped.length + '件 ' + qty + '个' + (res.box.palletLabelId !== 'NOPALLET' ? ' [托盘 ' + res.box.palletLabelId + ']' : ''), true);
        this.$emit('shipped', { box: v, shipped: res.shipped });
      } catch (e) {
        console.error('스캔 출고 오류:', e);
        this.log('✖ ' + v + ' — ' + e.message, false);
      } finally {
        this.busy = false;
        this.focus();
      }
    },
    async undoLast() {
      const last = this.lastOut;
      if (!last) return;
      if (!confirm('撤销 ' + last.label + ' 的出库吗？(이 박스의 출고완료를 출고대기로 되돌릴까요?)')) return;
      this.busy = true;
      try {
        const res = await undoScanOut(last);
        this.lastOut = null;
        this.log('↩ ' + last.label + ' 撤销(되돌림) ' + res.reverted.length + '件' + (res.skipped.length ? ' / 未撤销(못 되돌림) ' + res.skipped.length + ': ' + res.skipped[0].reason : ''), !res.skipped.length);
        this.$emit('shipped', { box: last.label, undo: true });
      } catch (e) {
        console.error('스캔 출고 되돌리기 오류:', e);
        this.log('✖ 撤销失败(되돌리기 실패) ' + last.label + ' — ' + e.message, false);
      } finally {
        this.busy = false;
        this.focus();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.pallet-scan {
  &__row { gap: 8px; }
  &__active {
    min-width: 170px;
    padding: 2px 10px;
    border: 2px solid #17a2b8;
    border-radius: 6px;
    background: #e8f7fa;
    small { color: #555; }
    div { font-size: 22px; font-weight: 700; line-height: 1.15; color: #117a8b; }
    &.is-empty { border-color: #ccc; background: #f6f6f6; div { color: #999; } }
    &.is-out { border-color: #dc3545; background: #fdecee; div { color: #b02a37; } }
  }
  &__list {
    max-height: 220px;
    overflow-y: auto;
    font-size: 12px;
    background: #f8f9fa;
    padding: 6px 8px;
    border-radius: 4px;
    white-space: pre-line;
  }
}
</style>
